import { IonButton, IonFooter, IonSpinner, IonToolbar, useIonToast } from '@ionic/react'
import { PatientsService } from 'infra/services/patients-service'
import { ReportsService } from 'infra/services/reports-service'
import { documentText } from 'ionicons/icons'
import { DefaultInput, ModalTitle, Typeahead } from 'presentation/components'
import { TypeaheadItem } from 'presentation/components/typeahead/typeahead'
import React, { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { modalNewReportAtom, ModalNewReportState, reportPatientAtom } from '../modal-new-report-atom'
import { UserInfo, UserRole } from 'infra/services/usecases/get-user-info'
import { ClinicsService } from 'infra/services/clinics-service'
import { Oncologist } from 'domain/usecases/get-oncologists'
import { userInfo } from 'os'

interface ConfirmPatientProps {
  userInfo: UserInfo
  patientsService: PatientsService
  reportsService: ReportsService
  clinicsService: ClinicsService
  onConfirmPatient: () => void
  onNewPatient: () => void
  dismiss: () => void
}

export type OncologistTypeaheadItem = {
  label: string
  value: string
  email: string
}

const ConfirmPatient: React.FC<ConfirmPatientProps> = (props) => {
  const { reportExtract } = useRecoilValue<ModalNewReportState>(modalNewReportAtom)
  const [patients, setPatients] = useState<TypeaheadItem[]>([])
  const [isLoadingPatients, setIsLoadingPatients] = useState<boolean>(false)
  const [confirmPatient, setConfirmPatient] = useRecoilState(reportPatientAtom)
  const [loading, setLoading] = useState(false)
  const [oncologists, setOncologists] = useState<OncologistTypeaheadItem[]>([])
  const [isLoadingOncologists, setIsLoadingOncologists] = useState<boolean>(false)
  const [presetToast] = useIonToast()

  const getPatients = (search?: string) => {
    setIsLoadingPatients(true)
    props.patientsService
      .getAllPatients({
        page: 1,
        size: 10,
        search,
      })
      .then((result) => {
        const newItems = result.items.map((item) => ({ label: item.name, value: item.MRN }))
        setPatients(newItems)
      })
      .catch(() => setPatients([]))
      .finally(() => setIsLoadingPatients(false))
  }

  const getOncologists = (search?: string) => {
    setIsLoadingOncologists(true)
    props.clinicsService
      .getOncologists({
        clinic_id: props.userInfo.clinic,
        page: 1,
        size: 10,
        search: search || ''
      })
      .then((result) => {
        const newItems = result.items.map((item) => ({ label: item?.name, value: item?.user_id, email: item?.email }))
        setOncologists(newItems)
      })
      .catch(() => setOncologists([]))
      .finally(() => setIsLoadingOncologists(false))
  }

  const handleConfirmPatient = () => {
    setLoading(true)
    props.reportsService
      .confirmPatientReport({
        ...confirmPatient,
        oncologistId: confirmPatient.oncologistId || (props.userInfo.id as string),
        reportId: reportExtract?.report_id as string
      })
      .then(() => props.onConfirmPatient())
      .catch(() => showToastError('There was an error trying to confirm patient, try again later.'))
      .finally(() => setLoading(false))
  }

  const showToastError = (message: string) => {
    presetToast({
      message,
      duration: 5000,
      color: 'danger',
    })
  }

  useEffect(() => {
    if(props.userInfo.role === UserRole.Oncologist) {
      getOncologists(props.userInfo.name)
    }
  }, [props.userInfo.role])

  useEffect(() => {
    setLoading(true)
    const oncologist = oncologists.find((oncologist) => oncologist?.email === props.userInfo.email)
    if(oncologist) {
      setConfirmPatient((s) => ({
        ...s,
        oncologist: oncologist.label as string,
        oncologistId: oncologist.value as string,
      }))
    }
    setLoading(false)
  }, [oncologists])



  return (
    <>
      <section className="ion-padding">
        <ModalTitle label="Confirm Patient" icon={documentText} />

        <span className="confirm-patient-subtitle">Select an existing patient name to confirm the merge</span>

        <Typeahead
          label="Patient"
          placeholder="Select..."
          options={patients}
          isLoading={isLoadingPatients}
          value={{ label: confirmPatient.name, value: confirmPatient.MRN }}
          onMenuOpen={() => getPatients()}
          onTypeaheadDebounce={(search) => getPatients(search)}
          onChange={(patient) => {
            const typeaheadItem = patient as TypeaheadItem

            setConfirmPatient((s) => ({
              ...s,
              name: typeaheadItem?.label as string,
              MRN: typeaheadItem?.value as string,
            }))
          }}
        />

        <DefaultInput
          name="MRN"
          state={confirmPatient}
          value={confirmPatient.MRN}
          setState={setConfirmPatient}
          labelText="Medical Record Number"
          placeholder="MRN"
          notEditable={true}
          notCleanable={true}
        />
        {
          props.userInfo.role === UserRole.MedicalAssistant && (
            <Typeahead
              label="Oncologist"
              placeholder="Select an oncologist..."
              options={oncologists}
              isLoading={isLoadingOncologists}
              value={{ label: confirmPatient.oncologist, value: confirmPatient.oncologist }}
              onMenuOpen={() => getOncologists()}
              onTypeaheadDebounce={(search) => getOncologists(search)}
              onChange={(oncologist) =>
                setConfirmPatient((s) => ({
                  ...s,
                  oncologist: (oncologist as TypeaheadItem)?.label as string,
                  oncologistId: (oncologist as TypeaheadItem)?.value as string,
                }))
              }
            />
          )
        }
      </section>

      <IonFooter>
        <IonToolbar>
          <IonButton slot="end" fill="outline" onClick={props.dismiss}>
            Cancel
          </IonButton>

          <IonButton slot="end" fill="outline" onClick={props.onNewPatient}>
            Create New Patient
          </IonButton>

          <IonButton
            slot="end"
            onClick={handleConfirmPatient}
            disabled={!confirmPatient?.MRN || !confirmPatient.name || loading}
          >
            {loading ? <IonSpinner /> : 'Confirm'}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default ConfirmPatient
